import React, { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Loader2, Eye, EyeOff, Building2, User, Lock, GraduationCap } from "lucide-react";
import { toast } from "sonner";
import { LoginCoursesTab } from "@/components/LoginCoursesTab";

const BAR = ["#facc15", "#f59e0b", "#f97316", "#ea580c", "#5eead4", "#84cc16", "#93c5fd", "#60a5fa"];

const SsmaSymbol = () => (
  <svg viewBox="0 0 64 64" className="h-14 w-14" data-testid="login-ssma-symbol" aria-label="Símbolo SSMA">
    <path d="M32 4 L56 13 V31 C56 46 45 56 32 60 C19 56 8 46 8 31 V13 Z" fill="#0f2a4d" />
    <path d="M32 4 L56 13 V31 L32 32 Z" fill="#f59e0b" />
    <path d="M32 4 L8 13 V31 L32 32 Z" fill="#facc15" />
    <path d="M32 32 L56 31 C56 46 45 56 32 60 Z" fill="#38bdf8" />
    <path d="M32 32 L8 31 C8 46 19 56 32 60 Z" fill="#84cc16" />
    <path d="M28 18 h8 v8 h8 v8 h-8 v8 h-8 v-8 h-8 v-8 h8 Z" fill="#fff" />
  </svg>
);

const Field = ({ icon: Icon, children }) => (
  <div className="flex h-11 items-center gap-3 rounded-full bg-white px-4 shadow-sm ring-1 ring-white/10 focus-within:ring-2 focus-within:ring-sky-400">
    <Icon className="h-5 w-5 shrink-0 text-slate-500" />
    {children}
  </div>
);

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [company, setCompany] = useState("Valbrac");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("empresa");

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      await login(email, password);
      toast.success("Bem-vindo ao VIGIA · SSMA Valbrac");
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const inputCls = "h-full w-full border-0 bg-transparent text-sm text-slate-700 outline-none placeholder:text-slate-400";

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#3f63a0] p-4 sm:p-8" data-testid="login-page">
      <style>{`@keyframes vigiaIn { from { opacity:0; transform: translateY(16px) } to { opacity:1; transform:none } }`}</style>

      <div className="relative w-full max-w-5xl overflow-hidden rounded-sm bg-[#0b1f3d] text-white shadow-2xl" style={{ animation: "vigiaIn .5s ease-out" }} data-testid="login-card">
        <div className="absolute inset-0 opacity-60" style={{ backgroundImage: "radial-gradient(circle at 30% 45%, #16365f 0, transparent 55%), radial-gradient(circle at 85% 30%, #132f55 0, transparent 50%)" }} />
        <img src="/valve-technician.jpg" alt="" data-testid="login-bg-technician"
          className={`pointer-events-none absolute inset-0 h-full w-full object-cover object-right transition-opacity duration-700 ${tab === "cursos" ? "opacity-30" : "opacity-20"}`} />

        <div className="relative grid gap-8 px-6 pb-14 pt-14 md:grid-cols-2 md:gap-4 md:px-12 md:pb-16 md:pt-12">
          <div className="flex flex-col items-center justify-center text-center">
            <img src="/vigia-logo.png" alt="Logo Valbrac SSMA" className="h-52 w-auto object-contain sm:h-64 md:h-72" data-testid="login-logo" />
            <h1 className="mt-4 text-4xl font-semibold tracking-[0.35em] text-slate-200 sm:text-5xl" data-testid="login-brand">VIGIA</h1>
            <p className="mt-2 text-[10px] font-medium uppercase tracking-[0.18em] text-sky-300/80">Vigilância Integrada de Gestão, Incidentes e Ambiente</p>
            <p className="mt-2 max-w-sm text-sm text-slate-300" data-testid="login-slogan">Sua guardiã inteligente de Segurança, Saúde e Meio Ambiente</p>
          </div>

          <div className="flex flex-col items-center justify-center">
            <div className="flex items-center gap-3" data-testid="login-header">
              <span className="text-2xl font-extrabold tracking-wide text-white sm:text-3xl">VALBRAC</span>
              <SsmaSymbol />
              <span className="text-2xl font-light tracking-wide text-white sm:text-3xl">SSMA</span>
            </div>

            <div className="mt-8 w-full max-w-xs">
              <Field icon={tab === "cursos" ? GraduationCap : Building2}>
                <select value={tab === "cursos" ? "cursos" : company} data-testid="login-company" className={inputCls}
                  onChange={(e) => { const v = e.target.value; if (v === "cursos") setTab("cursos"); else { setTab("empresa"); setCompany(v); } }}>
                  <option value="Valbrac">Valbrac</option>
                  <option value="cursos">Cursos EAD</option>
                </select>
              </Field>
            </div>

            {tab === "cursos" ? <div className="mt-3 w-full max-w-xs"><LoginCoursesTab /></div> : (
            <form onSubmit={submit} className="mt-3 w-full max-w-xs space-y-3" data-testid="login-form">
              <Field icon={User}>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="E-mail" required className={inputCls} data-testid="login-email" />
              </Field>
              <Field icon={Lock}>
                <input type={show ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Senha" required className={inputCls} data-testid="login-password" />
                <button type="button" onClick={() => setShow((s) => !s)} className="shrink-0 text-slate-500 hover:text-slate-700" data-testid="login-toggle-password">
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </Field>
              <div className="text-right">
                <Link to="/forgot-password" className="text-[11px] font-semibold text-white hover:underline" data-testid="forgot-link">Esqueci minha senha</Link>
              </div>

              {error && <p className="rounded-md bg-rose-500/15 px-3 py-2 text-xs text-rose-200" data-testid="login-error">{error}</p>}

              <div className="flex justify-center pt-2">
                <button type="submit" disabled={loading}
                  className="flex h-11 w-36 items-center justify-center rounded-full bg-gradient-to-b from-[#2f5f9e] to-[#1d3f70] text-lg font-medium text-white shadow-lg shadow-black/30 transition-transform hover:-translate-y-0.5 disabled:opacity-70" data-testid="login-submit">
                  {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Entrar"}
                </button>
              </div>
              <div className="mx-auto mt-4 h-6 w-full overflow-hidden rounded-sm border border-sky-900 bg-[#0f4a63]" data-testid="login-progress">
                <div className="h-full bg-sky-400/70 transition-[width] duration-[1500ms]" style={{ width: loading ? "90%" : "0%" }} />
              </div>
            </form>
            )}
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0">
          <div className="flex items-center justify-between px-5 pb-2 text-[11px] text-slate-400">
            <span>Desenvolvido por <span className="font-semibold text-emerald-400">Leandro Américo</span></span>
            <span className="text-white" data-testid="login-version">v1.0.{new Date().getFullYear()}</span>
          </div>
          <div className="flex h-2 w-full">
            {BAR.map((c, i) => <div key={i} className="flex-1" style={{ background: c }} />)}
          </div>
        </div>
      </div>
    </div>
  );
}
